import { SearchSuggestionsList } from '@iheartjane/menu';
import { SearchField } from '@iheartjane/reefer';
import { SEARCH_POSITION } from '@iheartjane/types';
import { clsx } from 'clsx';
import type { FocusEvent } from 'react';
import { useRef } from 'react';
import { Form } from 'react-router';

import { SearchManager } from '@jane/bloom/components/searchManager';
import { useFullDestination } from '@jane/bloom/hooks/navigation/useFullDestination';
import { useStoreSwitcher } from '@jane/bloom/hooks/storeSwitcher/useStoreSwitcher';
import { useCustomLanguage } from '@jane/bloom/hooks/useCustomLanguage';
import { useDebounce } from '@jane/bloom/hooks/useDebounce';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useUserSearchQuery } from '@jane/bloom/hooks/useUserSearchQuery';
import { trackHeaderTap } from '@jane/bloom/services/tracking/track';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

import { toSuggestionProduct } from './toSuggestionProduct';

import styles from './searchDropdown.module.css';

export function SearchDropdown({
  dropdownWidth,
}: {
  dropdownWidth?: string;
} = {}) {
  const {
    hideSearchSuggestions,
    searchModalQuery,
    setSearchModalQuery,
    shouldOpenSearchModal,
    showSearchSuggestions,
  } = useUserSearchQuery();
  const debouncedQuery = useDebounce(searchModalQuery, 300);
  const containerRef = useRef<HTMLDivElement>(null);

  const { featureFlags, goldConfig, pinnedStore, shoppingPreferences, theme } =
    useRootLoaderData();
  const { fulfillmentMode } = useStoreSwitcher();
  const customLanguage = useCustomLanguage();
  const generatePath = useFullDestination();

  const shouldTrackHeaderTap =
    theme?.bloom?.search?.position === SEARCH_POSITION.split ||
    theme?.bloom?.search?.position === SEARCH_POSITION.header;

  // Ensure that the dropdown does not close if someone is keyboard navigating within it
  const handleBlur = (e: FocusEvent) => {
    if (
      containerRef.current &&
      e.relatedTarget &&
      containerRef.current.contains(e.relatedTarget)
    ) {
      return;
    }
    hideSearchSuggestions();
  };

  return (
    <div ref={containerRef} className={styles['wrapper']} onBlur={handleBlur}>
      <Form
        action="/search"
        method="get"
        name="Search Form"
        onSubmit={() => hideSearchSuggestions()}
      >
        <SearchField
          className={styles['searchField']}
          label="Search"
          placeholder={`Search ${pinnedStore.name}`}
          name={SEARCH_TERM_QUERY_KEY}
          onChange={(val) => setSearchModalQuery(val)}
          isDebounced={false}
          borderRadius="lg"
          value={searchModalQuery}
          disableMobileInputStyling
          onFocus={() => {
            showSearchSuggestions('dropdown');
            shouldTrackHeaderTap &&
              trackHeaderTap(
                {
                  reservationMode: shoppingPreferences?.reservationMode,
                  tappable: 'search icon',
                },
                featureFlags
              );
          }}
        />
      </Form>
      <div
        className={clsx(styles['container'], {
          [styles['container--open']]:
            shouldOpenSearchModal === 'dropdown' && searchModalQuery,
        })}
        style={dropdownWidth ? { width: dropdownWidth } : undefined}
      >
        <SearchManager key={debouncedQuery} query={debouncedQuery}>
          {({ products, shouldFetchNextPage, totalProducts }) => (
            <SearchSuggestionsList
              isLoading={shouldFetchNextPage}
              onSearchLinkClick={hideSearchSuggestions}
              products={products.map((product) =>
                toSuggestionProduct({
                  customLanguage,
                  featureFlags,
                  fulfillmentMode,
                  generatePath,
                  goldConfig,
                  hideDropdown: hideSearchSuggestions,
                  pinnedStore,
                  product,
                  shoppingPreferences,
                })
              )}
              query={searchModalQuery}
              searchUrl={generatePath(
                `/search?${new URLSearchParams({
                  [SEARCH_TERM_QUERY_KEY]: searchModalQuery,
                })}`
              )}
              totalProducts={totalProducts}
            />
          )}
        </SearchManager>
      </div>
    </div>
  );
}
